import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { useUserProfile } from '@/hooks/useUserProfile';
import { toIntlLocale } from '@/lib/format';
import { tokenPercent, tokensRemaining } from '@/components/v11/accountHealth/tokenUsage';

/**
 * The consumption pill in the topbar: a bar, the percentage used, and what is
 * left of the period's allowance.
 *
 * Renders nothing when the account has no active token period — `accountHealth`
 * is null then, and an empty bar would read as "you have used nothing" rather
 * than "there is nothing to show".
 */
export const TokenUsagePill: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { userData } = useUserProfile();
  const health = userData.accountHealth ?? null;

  if (health === null) return null;

  const percent = tokenPercent(health.tokensUsed, health.tokensLimit);
  const remaining = tokensRemaining(health.tokensUsed, health.tokensLimit);
  const remainingLabel = new Intl.NumberFormat(toIntlLocale(i18n.language), {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(remaining);

  return (
    <div
      data-testid="token-usage-pill"
      // Desktop only: on a phone the topbar belongs to the branding and the
      // theme toggle, and the same numbers live on the Channels page.
      // Sizes follow Figma's token-meter (231:13871).
      className="hidden shrink-0 items-center gap-[9px] whitespace-nowrap rounded-[20px] border border-v11-border bg-v11-surface px-[14px] py-1.5 font-dm-sans text-[12px] leading-[normal] lg:flex"
    >
      <Clock className="h-3 w-3 text-v11-accent" aria-hidden="true" />
      <span
        className="h-1 w-[72px] overflow-hidden rounded-[2px] bg-v11-border"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('app.v11.shell.tokens.used', { percent })}
      >
        <span
          className="block h-full rounded-[2px] bg-v11-accent"
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </span>
      <span className="font-bold text-v11-text-primary">{percent}%</span>
      {/* The remaining count is the first thing to go when the topbar gets
          tight: the percentage carries the same warning in less room. The
          divider goes with it. */}
      <span aria-hidden="true" className="hidden h-3 w-px bg-v11-border xl:block" />
      <span className="hidden text-v11-text-subtle dark:text-v11-text-tertiary xl:inline">
        {t('app.v11.shell.tokens.remaining', { amount: remainingLabel })}
      </span>
    </div>
  );
};
